import React, { Fragment, useState, useEffect } from "react";
import { Card, Form, Spinner, Button, Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import Snackbars from "../../../utils/Snackbars";
import useRequests from "../../../utils/useRequests";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { useFormik } from "formik";
import * as Yup from "yup";

const WithdrawalTransactions = () => {
  const location = useLocation();
  const { getRequest, putRequest } = useRequests();

  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [qrCodeData, setQrCodeData] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [AlertData, setAlertData] = React.useState({
    variant: '',
    message: '',
  });

  const resetAlertData = () => {
    setTimeout(() => {
      setAlertData((AlertData) => ({
        ...AlertData,
        variant: '',
        message: '',
      }));
    }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
  };

  const [loading, setLoading] = useState(true);

  // Generate QR Code for UPI using canvas
  const generateQRCode = async (upiId, amount, name, message = '') => {
    try {
      // Dynamic import to avoid build issues
      const QRCode = await import('qrcode-generator');
      const qr = QRCode.default(0, 'M');

      let upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR`;
    
      if (message && message.trim()) {
        upiString += `&tn=${encodeURIComponent(message.trim())}`;
      }

      qr.addData(upiString);
      qr.make();

      // Create canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const moduleSize = 8;
      const moduleCount = qr.getModuleCount();
      const canvasSize = moduleCount * moduleSize;
      
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      // Fill background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      // Draw QR modules
      ctx.fillStyle = '#000000';
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (qr.isDark(row, col)) {
            ctx.fillRect(col * moduleSize, row * moduleSize, moduleSize, moduleSize);
          }
        }
      }

      return canvas.toDataURL();
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const handleUPIClick = async (transaction) => {
    setSelectedTransaction(transaction);
    setShowQRModal(true);
    const phone = `${transaction.phone}`;
    const message = `${process.env.REACT_APP_NAME || 'PeacockVerse'} Withdrawal - ${phone} - Rs ${transaction.amount}`;
    const qrCode = await generateQRCode(transaction.upi_id, transaction.amount, phone, message);
    setQrCodeData(qrCode);
  };

  const handleCloseQRModal = () => {
    setShowQRModal(false);
    setSelectedTransaction(null);
    setQrCodeData('');
  };

  const fetchWithdrawalTransactions = async () => {
    try {
      const response = await getRequest('withdrawal-transactions');
      if (response && response.code === 200) {
        setTransactions(response.data);
      } else {
        console.error("Failed to fetch withdrawal transactions:", response.message);
        setAlertData((AlertData) => ({
          ...AlertData,
          variant: 'error',
          message: response.message || 'Failed to fetch withdrawal transactions',
        }));
      }
    } catch (error) {
      console.error("Error fetching withdrawal transactions:", error);
      setAlertData((AlertData) => ({
        ...AlertData,
        variant: 'error',
        message: 'An error occurred while fetching withdrawal transactions',
      }));
    }
    resetAlertData();
    setLoading(false);
  };

  useEffect(() => {
    fetchWithdrawalTransactions();
    if (location.state && typeof location.state.message !== 'undefined') {
      setAlertData((AlertData) => ({
        ...AlertData,
        variant: location.state.status,
        message: location.state.message,
      }));
      resetAlertData();
      setLoading(false);
    }
  }, []);

  // Filter transactions based on the search query
  const filteredTransactions = transactions.filter((transaction) => {
    const phone = `${transaction.phone}`.toLowerCase();
    const name = `${transaction.first_name? transaction.first_name +" "+transaction.last_name : "N/A"}`.toLowerCase();
    const amount = `${transaction.amount}`;
    const upi_id = `${transaction.upi_id}`.toLowerCase();
    const status = `${transaction.status}`.toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    
    return phone.includes(searchLower) ||
           amount.includes(searchQuery) || upi_id.includes(searchLower) || 
           status.includes(searchLower);
  });

  // Form validation and submission
  const formik = useFormik({
    initialValues: {
      status: '',
      transaction_id: '',
      reason: '',
    },
    validationSchema: Yup.object({
      status: Yup.string().required('Status is required'),
      transaction_id: Yup.string().when('status', {
        is: 'Completed',
        then: () => Yup.string().required('Transaction ID is required for completed transactions'),
        otherwise: () => Yup.string()
      }),
      reason: Yup.string().when('status', {
        is: 'Rejected',
        then: () => Yup.string().required('Reason is required for rejected transactions'),
        otherwise: () => Yup.string()
      }),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      
      try {
        const submitData = {
          status: values.status,
          ...(values.status === 'Completed' && { transaction_id: values.transaction_id }),
          ...(values.status === 'Rejected' && { reason: values.reason }),
        };

        const response = await putRequest(`withdrawal-transaction-status/${selectedTransaction.uwt_id}`, JSON.stringify(submitData));

        if (response && response.code === 200) {
          const updatedTransactions = transactions.map((transaction) =>
            transaction.uwt_id === selectedTransaction.uwt_id 
              ? { ...transaction, ...submitData } 
              : transaction
          );
          setTransactions(updatedTransactions);
          
          setAlertData({
            variant: 'success',
            message: 'Withdrawal transaction status updated successfully!',
          });
          resetAlertData();
          handleCloseModal();
          fetchWithdrawalTransactions();
        } else {
          setAlertData({
            variant: 'error',
            message: response.message || 'Failed to update withdrawal transaction status.',
          });
          resetAlertData();
        }
      } catch (error) {
        console.error("Error updating status:", error);
        setAlertData({
          variant: 'error',
          message: 'An error occurred while updating the status.',
        });
        resetAlertData();
      } finally {
        setIsProcessing(false);
      }
    }
  });

  const handleStatusChange = (transaction) => {
    setSelectedTransaction(transaction);
    formik.setFieldValue('status', transaction.status || 'Pending');
    formik.setFieldValue('transaction_id', transaction.transaction_id || '');
    formik.setFieldValue('reason', transaction.reason || '');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
    setIsProcessing(false);
    formik.resetForm();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'btn-success';
      case 'Rejected':
        return 'btn-danger';
      case 'Pending':
      default:
        return 'btn-warning';
    }
  };

  const columns = [
    {
      name: "Full Name",
      selector: (row) => `${row.first_name ? row.first_name +" "+row.last_name : "N/A"}`,
      sortable: true,
      width: "250px"
    },
    {
      name: "Mobile Number",
      selector: (row) => `${row.phone}`,
      sortable: true,
      width: "200px"
    },
    
    {
      name: "Amount",
      selector: (row) => `₹${row.amount}`,
      sortable: true,
      width: "120px"
    },
    {
      name: "UPI ID",
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span 
            style={{ 
              cursor: 'pointer', 
              color: '#007bff',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
            onClick={() => handleUPIClick(row)}
            title="Click to view QR Code"
          >
            {row.upi_id}
          </span>
          <i 
            className="fa fa-qrcode" 
            style={{ 
              color: '#28a745', 
              cursor: 'pointer',
              fontSize: '16px'
            }}
            onClick={() => handleUPIClick(row)}
            title="View QR Code"
          ></i>
        </div>
      ),
      sortable: true,
      width: "220px"
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
          {row.status || 'Pending'}
        </span>
      ),
      sortable: true,
      width: "100px"
    },
    {
      name: "Date",
      selector: (row) => {
        const date = new Date(row.created_at);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
      sortable: true,
      width: "120px"
    },
    {
      name: "Action",
      cell: (row) => {
        const status = row.status || 'Pending';
        
        if (status === 'Pending') {
          return (
            <button
              className="btn btn-primary btn-xxs"
              onClick={() => handleStatusChange(row)}
              title="Update Status"
              disabled={isProcessing}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          );
        } else {
          return (
            <span 
              className={status === 'Completed' ? 'text-success' : 'text-danger'} 
              title={`Status: ${status}`}
            >
              {status === 'Completed' ? '✓' : '✗'}
            </span>
          );
        }
      },
      center: true,
      width: "100px",
    },
  ];

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headCells: {
      style: {
        color: "#341439",
        fontSize: "17px",
        fontWeight: "600",
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    cells: {
      style: {
        fontSize: "16px",
        padding: "12px 9px",
        color: "#7E7E7E",
      },
    },
    pagination: {
      style: {
        fontSize: "16px",
      },
    },
  };

  // CSV Export data
  const csvHeaders = [
    { label: "Full Name", key: "name" },
    { label: "Mobile Number", key: "phone" },
    { label: "Amount", key: "amount" },
    { label: "UPI ID", key: "upi_id" },
    { label: "Status", key: "status" },
    { label: "Date", key: "date" },
  ];

  const csvData = filteredTransactions.map((transaction) => {
    const formatDateTime = (date) => {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    return {
      phone: `${transaction.phone}`,
      name: `${transaction.first_name? transaction.first_name +" "+transaction.last_name : "N/A"}`,
      amount: `₹${transaction.amount}`,
      upi_id: transaction.upi_id,
      status: transaction.status || 'Pending',
      date: formatDateTime(transaction.created_at),
    };
  });

  return (
    <Fragment>
      <div className='d-sm-flex d-block justify-content-between align-items-center mb-4'>
        {AlertData.message !== "" ? (
          <Snackbars variant={AlertData.variant} message={AlertData.message} />
        ) : (
          ""
        )}
        <h2 className='text-black font-w600 mb-0'>Withdrawal Transactions</h2>
        <CSVLink data={csvData} headers={csvHeaders} filename={"withdrawal_transactions.csv"}>
          <Button variant="primary">Download CSV</Button>
        </CSVLink>
      </div>

      {/* Search input */}
      <div className='mb-4 d-sm-flex d-block justify-content-end'>
        <div style={{ position: "relative", width: "18rem" }}>
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              fontSize: "18px",
              fontWeight: 500,
              paddingRight: "2rem",
              borderRadius:'0.625rem',
              height:'3.2rem'
            }}
          />
          <i
            className="fa fa-search"
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#6c757d",
              pointerEvents: "none",
            }}
          ></i>
        </div>
      </div>

      <Card>
        <Card.Body>
          {loading ? (
            <div className='text-center'>
              <Spinner animation='border' />
              <p>Loading withdrawal transactions...</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredTransactions}
              customStyles={customStyles}
              pagination
              highlightOnHover
              responsive
            />
          )}
        </Card.Body>
      </Card>

      {/* QR Code Modal */}
      <Modal show={showQRModal} onHide={handleCloseQRModal} centered size="md">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f8f9fa', padding: '20px' }}>
          <Modal.Title style={{ color: '#2c3e50', fontWeight: '600' }}>
            <i className="fa fa-qrcode me-2" style={{ color: '#28a745' }}></i>
            UPI Payment QR Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px', textAlign: 'center', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          {selectedTransaction && (
            <div>
                <div className="mb-4">
                    <div style={{ 
                        background: 'white', 
                        borderRadius: '15px', 
                        padding: '25px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        border: '1px solid #dee2e6'
                    }}>
                        <h6 style={{ color: '#2c3e50', marginBottom: '15px', fontWeight: '600' }}>
                        Scan QR Code to Pay
                        </h6>
                        {qrCodeData ? (
                        <div>
                            <img 
                            src={qrCodeData} 
                            alt="UPI QR Code" 
                            style={{ 
                                maxWidth: '250px', 
                                height: 'auto',
                                border: '3px solid #dee2e6',
                                borderRadius: '10px',
                                padding: '10px',
                                background: 'white'
                            }} 
                            />
                            <p style={{ 
                            color: '#6c757d', 
                            fontSize: '13px', 
                            marginTop: '15px',
                            fontStyle: 'italic'
                            }}>
                            <i className="fa fa-info-circle me-1"></i>
                            Scan with any UPI app to make payment
                            </p>
                        </div>
                        ) : (
                        <div>
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span style={{ color: '#6c757d' }}>Generating QR Code...</span>
                        </div>
                        )}
                    </div>
                </div>

                <div style={{ 
                    background: 'white', 
                    borderRadius: '15px', 
                    padding: '20px', 
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    border: '1px solid #dee2e6'
                }}>
                    <h5 style={{ color: '#2c3e50', marginBottom: '15px', fontWeight: '600' }}>
                    Payment Details
                    </h5>
                    <div className="row text-start">
                    <div className="col-6">
                        <small style={{ color: '#6c757d', fontWeight: '500' }}>Recipient:</small>
                        <div style={{ color: '#2c3e50', fontWeight: '600', fontSize: '14px' }}>
                        {selectedTransaction.first_name? selectedTransaction.first_name +" "+selectedTransaction.last_name : selectedTransaction.phone}
                        </div>
                    </div>
                    <div className="col-6">
                        <small style={{ color: '#6c757d', fontWeight: '500' }}>Amount:</small>
                        <div style={{ color: '#28a745', fontWeight: '700', fontSize: '16px' }}>
                        ₹{selectedTransaction.amount}
                        </div>
                    </div>
                    </div>
                    <div className="mt-3">
                    <small style={{ color: '#6c757d', fontWeight: '500' }}>UPI ID:</small>
                    <div style={{ 
                        color: '#007bff', 
                        fontWeight: '600', 
                        fontSize: '14px',
                        background: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        marginTop: '5px',
                        border: '1px solid #dee2e6'
                    }}>
                        {selectedTransaction.upi_id}
                    </div>
                    </div>
                </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '2px solid #f8f9fa', padding: '15px 20px' }}>
          <Button 
            variant="light" 
            onClick={handleCloseQRModal}
            style={{ borderRadius: '8px', fontWeight: '500' }}
          >
            <i className="fa fa-times me-1"></i>
            Close
          </Button>
          {qrCodeData && (
            <Button 
              variant="success" 
              onClick={() => {
                const link = document.createElement('a');
                link.download = `qr-code-${selectedTransaction?.upi_id}.png`;
                link.href = qrCodeData;
                link.click();
              }}
              style={{ borderRadius: '8px', fontWeight: '500' }}
            >
              <i className="fa fa-download me-1"></i>
              Download QR
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Status Update Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Withdrawal Status</Modal.Title>
        </Modal.Header>
        <form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <strong>User:</strong> {selectedTransaction?.first_name? selectedTransaction?.first_name +" "+selectedTransaction?.last_name : selectedTransaction?.phone}
            </div>
            <div className="mb-3">
              <strong>Amount:</strong> ₹{selectedTransaction?.amount}
            </div>
            
            <div className="row form-group mb-3">
              <label className="col-sm-2 form-label">Status:</label>
              <div className="col-sm-9">
                <Form.Select
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.status && formik.errors.status}
                  style={{ 
                    fontSize: "16px",
                    height: "auto",
                    padding: "8px 12px"
                  }}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Rejected">Rejected</option>
                </Form.Select>
              </div>
            </div>

            {formik.values.status === 'Completed' && (
              <div className="form-group mb-3">
                <label className="form-label">Transaction ID:</label>
                <Form.Control
                  type="text"
                  name="transaction_id"
                  placeholder="Enter transaction ID"
                  value={formik.values.transaction_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.transaction_id && formik.errors.transaction_id}
                  style={{ 
                    fontSize: "16px",
                    padding: "8px 12px"
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.transaction_id}
                </Form.Control.Feedback>
              </div>
            )}

            {formik.values.status === 'Rejected' && (
              <div className="form-group mb-3">
                <label className="form-label">Reason:</label>
                <Form.Control
                  as="textarea"
                  name="reason"
                  placeholder="Enter reason for rejection"
                  rows={3}
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.reason && formik.errors.reason}
                  style={{ 
                    fontSize: "16px",
                    padding: "8px 12px",
                    resize: "vertical"
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.reason}
                </Form.Control.Feedback>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='light' onClick={handleCloseModal} disabled={isProcessing}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant='primary'
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Processing...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </Fragment>
  );
};

export default WithdrawalTransactions;